// Migration event types for analytics and debugging
export type MigrationEventType = 
  | 'version_mismatch_detected'
  | 'migration_completed'
  | 'migration_failed'
  | 'version_updated_no_migration'
  | 'version_updated_existing_user'
  | 'version_reset'
  | 'fresh_install'
  | 'data_validation_failed'
  | 'localStorage_migration'
  | 'database_migration';

export interface MigrationEvent {
  type: MigrationEventType;
  timestamp: string;
  data: any;
  userAgent?: string;
  url?: string;
}

export interface MigrationError {
  error: Error;
  context: any;
  timestamp: string;
  userAgent?: string;
  url?: string;
}

// In-memory storage for migration events (for debugging)
const migrationEvents: MigrationEvent[] = [];
const migrationErrors: MigrationError[] = [];

// Maximum number of events to keep in memory
const MAX_EVENTS = 50;
const MAX_ERRORS = 20;

/**
 * Logs a migration event for analytics and debugging
 */
export function logMigrationEvent(eventType: MigrationEventType, data: any): void {
  try {
    const event: MigrationEvent = {
      type: eventType,
      timestamp: new Date().toISOString(),
      data: sanitizeEventData(data),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Add to in-memory storage
    migrationEvents.push(event);
    
    // Keep only recent events
    if (migrationEvents.length > MAX_EVENTS) {
      migrationEvents.shift();
    }

    // Console logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Migration Event] ${eventType}:`, data);
    }

    // Store recent events in localStorage for debugging
    try {
      localStorage.setItem('mpay_migration_events', JSON.stringify(migrationEvents.slice(-10)));
    } catch (e) {
      // Ignore localStorage errors
    }

    // TODO: Send to analytics service in production
    // sendToAnalytics(event);

  } catch (error) {
    console.error('Error logging migration event:', error);
  }
}

/**
 * Logs migration errors for debugging and monitoring
 */
export function logMigrationError(error: Error, context: any): void {
  try {
    const errorLog: MigrationError = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      } as Error,
      context: sanitizeEventData(context),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Add to in-memory storage
    migrationErrors.push(errorLog);
    
    // Keep only recent errors
    if (migrationErrors.length > MAX_ERRORS) {
      migrationErrors.shift();
    }

    // Console logging
    console.error('[Migration Error]', error.message, context);

    // Store recent errors in localStorage for debugging
    try {
      localStorage.setItem('mpay_migration_errors', JSON.stringify(migrationErrors.slice(-5)));
    } catch (e) {
      // Ignore localStorage errors
    }

    // TODO: Send to error monitoring service
    // sendToErrorMonitoring(errorLog);

  } catch (logError) {
    console.error('Error logging migration error:', logError);
  }
}

/**
 * Sanitizes event data to remove sensitive information
 */
function sanitizeEventData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  try {
    const sanitized = { ...data };
    
    // Remove sensitive fields
    const sensitiveFields = ['email', 'phone', 'firstName', 'lastName', 'password', 'token'];
    sensitiveFields.forEach(field => {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Truncate large objects
    const maxStringLength = 1000;
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'string' && sanitized[key].length > maxStringLength) {
        sanitized[key] = sanitized[key].substring(0, maxStringLength) + '...[TRUNCATED]';
      }
    });

    return sanitized;
  } catch (error) {
    return '[SANITIZATION_ERROR]';
  }
}

/**
 * Gets all migration events for debugging
 */
export function getMigrationEvents(): MigrationEvent[] {
  return [...migrationEvents];
}

/**
 * Gets all migration errors for debugging
 */
export function getMigrationErrors(): MigrationError[] {
  return [...migrationErrors];
}

/**
 * Clears all stored migration events and errors
 */
export function clearMigrationLogs(): void {
  migrationEvents.length = 0;
  migrationErrors.length = 0;
  
  try {
    localStorage.removeItem('mpay_migration_events');
    localStorage.removeItem('mpay_migration_errors');
  } catch (error) {
    console.error('Error clearing migration logs:', error);
  }
}

/**
 * Gets migration statistics for debugging
 */
export function getMigrationStats(): {
  totalEvents: number;
  totalErrors: number;
  eventsByType: Record<string, number>;
  recentEvents: MigrationEvent[];
  recentErrors: MigrationError[];
} {
  const eventsByType: Record<string, number> = {};
  
  migrationEvents.forEach(event => {
    eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
  });

  return {
    totalEvents: migrationEvents.length,
    totalErrors: migrationErrors.length,
    eventsByType,
    recentEvents: migrationEvents.slice(-5),
    recentErrors: migrationErrors.slice(-3)
  };
}

/**
 * Exports migration data for support/debugging
 */
export function exportMigrationData(): {
  version: string;
  timestamp: string;
  events: MigrationEvent[];
  errors: MigrationError[];
  stats: ReturnType<typeof getMigrationStats>;
} {
  return {
    version: '1.0.0',
    timestamp: new Date().toISOString(),
    events: getMigrationEvents(),
    errors: getMigrationErrors(),
    stats: getMigrationStats()
  };
}

/**
 * Development helper to log migration state
 */
export function debugMigrationState(): void {
  if (process.env.NODE_ENV === 'development') {
    console.group('🔄 Migration Debug Info');
    console.log('Stats:', getMigrationStats());
    console.log('Recent Events:', migrationEvents.slice(-3));
    console.log('Recent Errors:', migrationErrors.slice(-2));
    console.groupEnd();
  }
}